import { useTranslation } from 'react-i18next';

import { Avatar, Flex, HStack, Text } from '@bonusly/design-system';

import type { BizyRecognizeMomentNode } from '../../../lib/bizy/types';

// Display for the `recognize_moment` user node (BIZY-651). Renders the
// "Let's recognize {name} for what you noticed:" chip row followed by the full
// moment text, shown in place of the raw user text after the People Browser
// "Let's dive in" (moment added in BIZY-668; older persisted turns have no
// moment and render just the chip row). The observed-moment context Bizy
// drafts from travels separately in the request context — not here.

export interface BizyRecognizeMomentChipProps {
	person: BizyRecognizeMomentNode['payload']['person'];
	moment?: string | null;
	fontSize?: string | object;
}

export function BizyRecognizeMomentChip({ person, moment, fontSize }: BizyRecognizeMomentChipProps) {
	const { t } = useTranslation('admin_bizy');

	return (
		<Flex alignSelf="flex-end" maxW="100%" data-component="bizy-recognize-moment-chip">
			<Flex
				direction="column"
				gap={2}
				bg="secondary.bgAlpha"
				borderRadius={12}
				borderBottomRightRadius={4}
				px={3}
				py={2}
				boxShadow="0 1px 1px 0 rgba(0, 0, 0, 0.12)"
			>
				<Flex alignItems="center" flexWrap="wrap" columnGap={2} rowGap={1}>
					<Text fontSize={fontSize} color="text.default" whiteSpace="nowrap">
						{t('personView.letsRecognizePrefix')}
					</Text>
					<HStack spacing={1.5} bg="primary.bgAlpha" border="1px solid" borderColor="primary.bgAlphaHover" borderRadius="6px" pl={1} px={1.5} py={0.5}>
						<Avatar name={person.name} src={person.avatarUrl ?? undefined} boxSize="18px" flexShrink={0} />
						<Text fontSize={fontSize} fontWeight="semibold" color="text.default" whiteSpace="nowrap">
							{person.name}
						</Text>
					</HStack>
					{moment && (
						<Text fontSize={fontSize} color="text.default">
							{t('personView.forWhatYouNoticed')}
						</Text>
					)}
				</Flex>
				{moment && (
					<Text fontSize={fontSize} color="text.default" whiteSpace="pre-wrap">
						{moment}
					</Text>
				)}
			</Flex>
		</Flex>
	);
}
